// ───────────────────────────────────────────────
// 📦 useFileUpload (thumbnails 정렬 + 오류 ZIP 처리 안정화 + 로직 로그 강화)
// ───────────────────────────────────────────────

import { useCallback, useEffect, useRef, useState } from "react";
import JSZip from "jszip";

const BACKURL = process.env.REACT_APP_BACKEND_URL;

// Development build에서만 업로드 디버그 로그를 출력한다.
const DEBUG = process.env.NODE_ENV === "development";
const INVALID_UNCOMPRESSED_FILE_SIZE_LIMIT_BYTES = 1000;

// ───────────────────────────────────────────────
// 🧾 네 스타일 로그 유틸
// ───────────────────────────────────────────────
function logStep(title, data) {
  if (!DEBUG) return;
  if (data !== undefined) console.log(title, data);
  else console.log(title);
}

function logError(title, err) {
  if (!DEBUG) return;
  console.error(title, err);
}

export function useFileUpload(setIsUploadLoading) {
  const [uploadedFiles, setUploadedFilesRaw] = useState({});
  const [thumbnailsRaw, setThumbnailsRaw] = useState([]);
  const [faildList, setFaildList] = useState({});

  // -----------------------------------------------------------
  // 🔧 thumbnails setter (항상 오름차순 정렬)
  // -----------------------------------------------------------
  // ✅ 의도: 업로드 순서가 꼬여도 UI에서 썸네일이 항상 정렬된 상태로 보이게
  const setThumbnails = useCallback((updater) => {
    setThumbnailsRaw((prev) => {
      const next =
        typeof updater === "function" ? updater(prev) : (updater ?? []);

      return [...next].sort((a, b) => {
        const na = Number(String(a?.name ?? "").replace(/[^0-9]/g, ""));
        const nb = Number(String(b?.name ?? "").replace(/[^0-9]/g, ""));
        return na - nb;
      });
    });
  }, []);

  // -----------------------------------------------------------
  // 🔧 uploadedFiles setter (항상 key 오름차순 정렬)
  // -----------------------------------------------------------
  // ✅ 의도: uploadedFiles 자체도 key 기반으로 정렬된 상태 유지 (diff 계산 안정)
  const setUploadedFiles = useCallback((updater) => {
    setUploadedFilesRaw((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      if (!next) return {};

      const sorted = Object.entries(next).sort(
        ([a], [b]) => Number(a) - Number(b),
      );

      return Object.fromEntries(sorted);
    });
  }, []);

  const prevUploadedRef = useRef({});

  // ===========================================================
  // 🎯 EFFECT: uploadedFiles 변경 감지 → (diff) → 신규 ZIP 분석 → 썸네일 처리
  // ===========================================================
  useEffect(() => {
    // ───────────────────────────────────────────────
    // [STEP 1] : prev / curr 비교해서 added / removed 계산
    // ───────────────────────────────────────────────
    const prev = prevUploadedRef.current;
    const curr = uploadedFiles;

    const prevKeys = Object.keys(prev);
    const currKeys = Object.keys(curr);

    const addedKeys = currKeys.filter((k) => !prevKeys.includes(k));
    const removedKeys = prevKeys.filter((k) => !currKeys.includes(k));

    logStep("──────────────────────────────────────────────");
    logStep("[STEP 1] : uploadedFiles diff 계산");
    logStep("prevKeys =", prevKeys);
    logStep("currKeys =", currKeys);
    logStep("addedKeys =", addedKeys);
    logStep("removedKeys =", removedKeys);

    // ───────────────────────────────────────────────
    // [STEP 1-REMOVE] : 삭제된 파일 처리 (썸네일 제거 + prevRef에서도 제거)
    // ───────────────────────────────────────────────
    if (removedKeys.length > 0) {
      logStep("[STEP 1-REMOVE] : 삭제된 key 썸네일 제거 시작", removedKeys);

      setThumbnails((prevThumbs) =>
        prevThumbs.filter((t) => !removedKeys.includes(t.name)),
      );

      removedKeys.forEach((key) => delete prevUploadedRef.current[key]);

      logStep("[STEP 1-REMOVE-SUCCESS] : 삭제 반영 완료");
    }

    // 추가된게 없으면 더 할 거 없음
    if (addedKeys.length === 0) {
      logStep("[STEP 1-END] : 추가된 key 없음 → 종료");
      prevUploadedRef.current = curr;
      return;
    }

    // ===========================================================
    // ✅ run(): addedKeys만 대상으로 파이프라인 실행
    // ===========================================================
    const run = async () => {
      logStep("──────────────────────────────────────────────");
      logStep("[STEP 2] : ZIP 분석 & 썸네일 처리 시작");
      setIsUploadLoading(true);

      const newThumbs = []; // ✅ 이번 턴에서 새로 추가될 썸네일들
      const videoQueue = []; // ✅ 비디오 썸네일(백엔드 생성)이 필요한 항목 큐

      // ===========================================================
      // [STEP 2-LOOP] : addedKeys 순회
      // ===========================================================
      for (const key of addedKeys) {
        logStep("──────────────────────────────────────────────");
        logStep(`[STEP 2-LOOP] : 처리 시작 key=${key}`);

        const item = curr[key];
        const { file, image_thumbnail } = item || {};

        // ───────────────────────────────────────────────
        // [STEP 2-0] : 백엔드 썸네일이 이미 있는 경우
        // ───────────────────────────────────────────────
        // ✅ 의도: 이미 생성된 비디오 썸네일(image_thumbnail)이 있으면 ZIP 열지 않고 바로 썸네일 반영
        if (image_thumbnail) {
          logStep(
            `[STEP 2-0] : image_thumbnail 존재 → ZIP 분석 생략 (비디오로 처리)`,
            {
              key,
              image_thumbnail,
            },
          );

          newThumbs.push({ name: key, url: image_thumbnail, type: "video" });
          continue; // 🔥 다음 파일 처리
        }

        // ───────────────────────────────────────────────
        // [STEP 2-1] : ZIP 로딩
        // ───────────────────────────────────────────────
        let zip;
        try {
          logStep(`[STEP 2-1] : ZIP loadAsync 시작 key=${key}`);
          zip = await JSZip().loadAsync(file);
          logStep(`[STEP 2-1-SUCCESS] : ZIP 로딩 완료 key=${key}`);
        } catch (err) {
          logError(`[STEP 2-1-FAIL] : ZIP load 실패 key=${key}`, err);

          // 🔥 실패 목록에 추가
          setFaildList((prev) => ({
            ...prev,
            [key]: {
              fileName: key,
              reasonKey:
                "projectDetail.fileUpload.failedList.reasonUnreadableZip",
            },
          }));

          // 🔥 업로드 목록에서 제거 (UI에서도 사라지게)
          setUploadedFiles((prev) => {
            const clone = { ...prev };
            delete clone[key];
            return clone;
          });

          logStep(
            `[STEP 2-1-FAIL-END] : faildList 등록 + uploadedFiles 제거 완료 key=${key}`,
          );
          continue; // 🔥 다음 파일
        }

        // ───────────────────────────────────────────────
        // [STEP 2-2] : ZIP 내부 파일 리스트 수집
        // ───────────────────────────────────────────────
        const entries = Object.values(zip.files).filter((e) => !e.dir);
        const fileNames = entries.map((e) =>
          e.name.split("/").pop().toLowerCase(),
        );

        logStep(`[STEP 2-2] : ZIP entries 수집 완료 key=${key}`, {
          total: entries.length,
          sample: fileNames.slice(0, 10),
        });

        // ───────────────────────────────────────────────
        // [STEP 2-3] : 이미지 ZIP 판별 (csv/fft/u.png/v.png)
        // ───────────────────────────────────────────────
        const hasCSV = fileNames.some((n) => n.endsWith(".csv"));
        const hasFFT = fileNames.some((n) => n.endsWith(".fft"));
        const hasU = fileNames.some((n) => n.endsWith("u.png"));
        const hasV = fileNames.some((n) => n.endsWith("v.png"));

        const isImageValid = hasCSV && hasFFT && hasU && hasV; // ✅ 필수 완비
        const isImageZIP = hasCSV || hasU || hasV; // ✅ 이미지 계열 힌트

        // ───────────────────────────────────────────────
        // [STEP 2-4] : 비디오 ZIP 판별 (mp4 또는 avi/wav/fft)
        // ───────────────────────────────────────────────
        const hasMP4 = fileNames.some((n) => n.endsWith(".mp4"));
        const hasAVI = fileNames.some((n) => n.endsWith(".avi"));
        const hasWAV = fileNames.some((n) => n.endsWith(".wav"));
        const hasFFT2 = hasFFT; // ✅ fft는 이미지/비디오 둘 다 공유

        const hasVideoFile = hasMP4 || hasAVI;
        const isVideoValid = hasVideoFile && hasWAV && hasFFT2;
        const isVideoZIP = hasVideoFile || hasWAV;

        logStep(`[STEP 2-3~4] : ZIP 타입 판별 key=${key}`, {
          hasCSV,
          hasFFT,
          hasU,
          hasV,
          hasMP4,
          hasAVI,
          hasWAV,
          isImageZIP,
          isImageValid,
          isVideoZIP,
          isVideoValid,
        });

        // ===========================================================
        // [STEP 2-IMAGE-VALIDATE] : 이미지 ZIP인데 누락/손상 검사
        // ===========================================================
        if (isImageZIP) {
          logStep(`[STEP 2-IMAGE-VALIDATE] : 이미지 ZIP 검사 시작 key=${key}`);

          const missing = [];
          if (!hasCSV) missing.push(".csv");
          if (!hasFFT) missing.push(".fft");
          if (!hasU) missing.push("U.png");
          if (!hasV) missing.push("V.png");

          // *** 1) 필수 파일 모두 존재하는 경우 → 파일 크기(1000B 이하) 검사 ***
          if (isImageValid) {
            logStep(
              `[STEP 2-IMAGE-VALIDATE-1] : 필수 파일 OK → 1000B 이하 검사 key=${key}`,
            );

            const smallFiles = entries.filter((e) => {
              const size = e._data?.uncompressedSize || 0;
              return size <= INVALID_UNCOMPRESSED_FILE_SIZE_LIMIT_BYTES;
            });

            if (smallFiles.length > 0) {
              const smallList = smallFiles
                .map((f) => `${f.name} (${f._data?.uncompressedSize ?? 0}B)`)
                .join(", ");

              logStep(
                `[STEP 2-IMAGE-VALIDATE-FAIL] : 1000B 이하 파일 발견 key=${key}`,
                smallList,
              );

              setFaildList((prev) => ({
                ...prev,
                [key]: {
                  fileName: key,
                  fileType: "image",
                  reasonKey:
                    "projectDetail.fileUpload.failedList.reasonInvalidFileSize",
                  reasonParams: { files: smallList },
                },
              }));

              setUploadedFiles((prev) => {
                const clone = { ...prev };
                delete clone[key];
                return clone;
              });

              logStep(
                `[STEP 2-IMAGE-VALIDATE-FAIL-END] : faildList 등록 + uploadedFiles 제거 완료 key=${key}`,
              );
              continue;
            }

            logStep(
              `[STEP 2-IMAGE-VALIDATE-1-SUCCESS] : 파일 크기 정상 key=${key}`,
            );
          }

          // *** 2) 필수 파일 누락된 경우 → 실패 처리 ***
          if (!isImageValid) {
            logStep(
              `[STEP 2-IMAGE-VALIDATE-FAIL] : 필수 파일 누락 key=${key}`,
              missing,
            );

            setFaildList((prev) => ({
              ...prev,
              [key]: {
                fileName: key,
                fileType: "image",
                reasonKey:
                  "projectDetail.fileUpload.failedList.reasonMissingDataFiles",
                reasonParams: { files: missing.join(", ") },
              },
            }));

            setUploadedFiles((prev) => {
              const clone = { ...prev };
              delete clone[key];
              return clone;
            });

            logStep(
              `[STEP 2-IMAGE-VALIDATE-FAIL-END] : faildList 등록 + uploadedFiles 제거 완료 key=${key}`,
            );
            continue;
          }
        }

        // ===========================================================
        // [STEP 2-VIDEO-VALIDATE] : 비디오 ZIP인데 누락/손상 검사
        // ===========================================================
        if (isVideoZIP) {
          logStep(`[STEP 2-VIDEO-VALIDATE] : 비디오 ZIP 검사 시작 key=${key}`);

          const missing = [];
          if (!hasVideoFile) missing.push(".mp4 or .avi");
          if (!hasWAV) missing.push(".wav");
          if (!hasFFT2) missing.push(".fft");

          // *** 1) 필수 파일 모두 존재 → 파일 크기(1000B 이하) 검사 ***
          if (isVideoValid) {
            logStep(
              `[STEP 2-VIDEO-VALIDATE-1] : 필수 파일 OK → 1000B 이하 검사 key=${key}`,
            );

            const smallFiles = entries.filter((e) => {
              const size = e._data?.uncompressedSize || 0;
              return size <= INVALID_UNCOMPRESSED_FILE_SIZE_LIMIT_BYTES;
            });

            if (smallFiles.length > 0) {
              const smallList = smallFiles
                .map((f) => `${f.name} (${f._data?.uncompressedSize}B)`)
                .join(", ");

              logStep(
                `[STEP 2-VIDEO-VALIDATE-FAIL] : 1000B 이하 파일 발견 key=${key}`,
                smallList,
              );

              setFaildList((prev) => ({
                ...prev,
                [key]: {
                  fileName: key,
                  fileType: "video",
                  reasonKey:
                    "projectDetail.fileUpload.failedList.reasonInvalidFileSize",
                  reasonParams: { files: smallList },
                },
              }));

              setUploadedFiles((prev) => {
                const clone = { ...prev };
                delete clone[key];
                return clone;
              });

              logStep(
                `[STEP 2-VIDEO-VALIDATE-FAIL-END] : faildList 등록 + uploadedFiles 제거 완료 key=${key}`,
              );
              continue;
            }

            logStep(
              `[STEP 2-VIDEO-VALIDATE-1-SUCCESS] : 파일 크기 정상 key=${key}`,
            );
          }

          // *** 2) 필수 파일 누락 → 실패 처리 ***
          if (!isVideoValid) {
            logStep(
              `[STEP 2-VIDEO-VALIDATE-FAIL] : 필수 파일 누락 key=${key}`,
              missing,
            );

            setFaildList((prev) => ({
              ...prev,
              [key]: {
                fileName: key,
                fileType: "video",
                reasonKey:
                  "projectDetail.fileUpload.failedList.reasonMissingDataFiles",
                reasonParams: { files: missing.join(", ") },
              },
            }));

            setUploadedFiles((prev) => {
              const clone = { ...prev };
              delete clone[key];
              return clone;
            });

            logStep(
              `[STEP 2-VIDEO-VALIDATE-FAIL-END] : faildList 등록 + uploadedFiles 제거 완료 key=${key}`,
            );
            continue;
          }
        }

        // ===========================================================
        // [STEP 3-IMAGE] : 정상 이미지 ZIP → V(비전) 이미지로 썸네일 생성
        // ===========================================================
        if (isImageValid) {
          logStep(`[STEP 3-IMAGE] : 정상 이미지 ZIP → 썸네일 생성 key=${key}`);

          for (const entry of entries) {
            const fileName = entry.name.split("/").pop().toLowerCase();

            // ✅ V.png(비전 이미지)를 대표 썸네일로 사용
            if (fileName.endsWith("v.png")) {
              const blob = await entry.async("blob");
              const url = URL.createObjectURL(blob);

              newThumbs.push({
                name: key,
                url,
                type: "image",
              });

              logStep(
                `[STEP 3-IMAGE-SUCCESS] : 썸네일 생성 완료 key=${key}`,
                fileName,
              );
              break; // 🔥 첫 V 이미지만 사용
            }
          }

          continue; // 🔥 다음 파일
        }

        // ===========================================================
        // [STEP 3-VIDEO] : 정상 비디오 ZIP → MP4/AVI 추출 → 백엔드 썸네일 생성 큐
        // ===========================================================
        if (isVideoValid) {
          logStep(
            `[STEP 3-VIDEO] : 정상 비디오 ZIP → MP4/AVI 추출 시작 key=${key}`,
          );

          let videoBlob = null;
          let videoExtension = "mp4";

          for (const entry of entries) {
            const fileName = entry.name.split("/").pop().toLowerCase();
            if (fileName.endsWith(".mp4") || fileName.endsWith(".avi")) {
              videoBlob = await entry.async("blob");
              videoExtension = fileName.endsWith(".avi") ? "avi" : "mp4";
              logStep(
                `[STEP 3-VIDEO-SUCCESS] : MP4/AVI 추출 완료 key=${key}`,
                entry.name,
              );
              break;
            }
          }

          // ✅ UI 깜빡임 방지: 일단 placeholder(빈 url) 썸네일 먼저 추가
          newThumbs.push({ name: key, url: null, type: "video" });

          // ✅ 실제 썸네일은 백엔드에서 생성하므로 queue에 넣고 나중에 업데이트
          videoQueue.push({
            name: key,
            video: videoBlob,
            extension: videoExtension,
          });

          logStep(`[STEP 3-VIDEO-QUEUE] : videoQueue 적재 key=${key}`, {
            hasVideo: !!videoBlob,
            videoExtension,
          });

          continue; // 🔥 다음 파일
        }

        // ===========================================================
        // [STEP 3-UNKNOWN] : 이미지도 비디오도 아닌 애매한 ZIP
        // ===========================================================
        logStep(
          `[STEP 3-UNKNOWN] : 이미지/비디오 판별 불가 (그대로 둠) key=${key}`,
        );
      }

      // ───────────────────────────────────────────────
      // [STEP 4] : 이번 턴에서 생성된 newThumbs 반영
      // ───────────────────────────────────────────────
      if (newThumbs.length > 0) {
        logStep("[STEP 4] : newThumbs 반영", newThumbs);
        setThumbnails((prev) => [...prev, ...newThumbs]);
      } else {
        logStep("[STEP 4] : newThumbs 없음");
      }

      // ───────────────────────────────────────────────
      // [STEP 5] : videoQueue 처리 (백엔드에 MP4 업로드 → 썸네일 url 받기)
      // ───────────────────────────────────────────────
      if (videoQueue.length > 0) {
        logStep(
          "[STEP 5] : videoQueue 처리 시작",
          videoQueue.map((v) => v.name),
        );
      }

      for (const item of videoQueue) {
        const formData = new FormData();
        formData.append("mp4", item.video, `${item.name}.${item.extension}`);
        formData.append("name", item.name);

        logStep(`[STEP 5-REQUEST] : video_preview 요청 key=${item.name}`);

        try {
          const res = await fetch(`${BACKURL}/project/video_preview`, {
            method: "POST",
            credentials: "include",
            body: formData,
          });

          const data = await res.json();

          // ✅ 성공: placeholder 썸네일 url 업데이트 + uploadedFiles에 image_thumbnail 저장
          if (res.ok && data?.thumbnail_image) {
            const url = data.thumbnail_image;

            // 1) thumbnails placeholder 업데이트
            setThumbnails((prev) =>
              prev.map((t) => (t.name === item.name ? { ...t, url } : t)),
            );

            // 2) uploadedFiles에도 image_thumbnail 저장 (다음부터 ZIP 분석 생략 가능)
            setUploadedFiles((prev) => ({
              ...prev,
              [item.name]: {
                ...prev[item.name],
                image_thumbnail: url,
              },
            }));
          } else {
            logStep(
              `[STEP 5-FAIL] : video_preview 응답은 왔지만 성공 조건 불충족 key=${item.name}`,
              {
                ok: res.ok,
                data,
              },
            );
          }
        } catch (err) {
          logError(
            `[STEP 5-ERROR] : 영상 썸네일 생성 오류 key=${item.name}`,
            err,
          );
        }
      }

      // ───────────────────────────────────────────────
      // [STEP 6] : 로딩 종료
      // ───────────────────────────────────────────────
      setIsUploadLoading(false);
      logStep("[STEP 6] : setIsUploadLoading(false) 완료");
      logStep("──────────────────────────────────────────────");
    };

    run();

    // ✅ 마지막에 prevUploadedRef 갱신 (다음 diff 계산 기준)
    prevUploadedRef.current = curr;
  }, [setIsUploadLoading, setThumbnails, setUploadedFiles, uploadedFiles]);

  return {
    uploadedFiles,
    setUploadedFiles,
    thumbnails: thumbnailsRaw,
    setThumbnails,
    faildList,
    setFaildList,
  };
}
